/// <reference path="../vite-env.d.ts" />
import axios from 'axios';
import { extractRequestId } from './headers';
import { useAuthStore } from '../stores/authStore';

// VITE_API_BASE 미주입(undefined/빈값) 시에도 "/undefined/api" 가 되지 않도록 방어.
// 빈 값이면 same-origin 상대경로("/api") 로 동작한다(dev: nginx /api→FlowGate, 로컬: vite proxy).
const API_BASE = import.meta.env.VITE_API_BASE || '';

export const apiClient = axios.create({
  baseURL: `${API_BASE}/api`,
  timeout: 10000,
  withCredentials: true,
  // GatePass refresh TTL 플랫폼 분리 — 웹 로그인은 항상 WEB(1일). 다른 서비스는 무시.
  headers: { 'X-Client-Type': 'WEB' },
});

// 인증 면제 경로 — 토큰 미부착 (FlowGate SecurityConfig permitAll 정합)
const AUTH_EXEMPT_PATHS = [
  '/gatepass/login', 
  '/gatepass/refresh', 
  '/gatepass/social', 
  '/member/join',
  '/schedule/schedule-shares/me',
  '/worklog/work-order-shares/me', // 반장 작업요청 안내(외부 공유쿠키) — API명세 §2.14
  '/worklog/customer-report-shares/me', // 고객 공개 리포트(외부 공유쿠키) — API명세 §4.3
  '/indipia/customer-home/me', // 고객대시보드 진입 조회(외부 공유쿠키) — 설계서 §7.6
  '/indipia/customer-home/visit', // 고객대시보드 방문 기록(외부 공유쿠키) — 설계서 §7.6.4
  '/schedule/order-shares/me', // 거래처·반장 외부 링크(공유 쿠키)
  '/worklog/receipt-shares/me', // 거래처·반장 외부 링크(공유 쿠키)
];

function isAuthExempt(url?: string): boolean {
  if (!url) return false;
  return AUTH_EXEMPT_PATHS.some((p) => url.includes(p));
}

// 다른 기기 로그인에 의한 세션 축출 등 — refresh 재시도 없이 즉시 하드 로그아웃 대상 코드.
//   SESSION_SUPERSEDED : 같은 채널로 다른 기기에서 로그인해 이 세션이 밀려남
//   CHANNEL_REQUIRED   : 채널 헤더(X-Client-Type) 누락 — 정상 FE에선 안 나지만 방어적으로 처리
const HARD_LOGOUT_CODES = ['SESSION_SUPERSEDED', 'CHANNEL_REQUIRED'];

// 로그인 화면이 이 플래그를 읽어 "다른 기기에서 로그인되어 로그아웃되었습니다." 안내를 노출한다.
export const SESSION_KICK_FLAG = 'auth:sessionKicked';

// 401 응답 바디에서 에러 코드를 뽑는다. 표준 envelope(C-03 §4.2)는 flat `code`지만
// 게이트웨이가 error.code 형태로 줄 수도 있어 둘 다 방어적으로 확인한다.
function extractErrorCode(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const body = data as { code?: unknown; error?: { code?: unknown } };
  const code = body.error?.code ?? body.code;
  return typeof code === 'string' ? code : undefined;
}

// 하드 로그아웃 — 인증 스토어 클리어(토큰·user 제거) + 안내 플래그.
// 스토어가 비워지면 RequireAuth가 반응적으로 /login 으로 라우팅한다.
function forceHardLogout(): void {
  try {
    sessionStorage.setItem(SESSION_KICK_FLAG, '1');
  } catch {
    /* private 모드 등 sessionStorage 불가 — 안내만 생략하고 로그아웃은 진행 */
  }
  useAuthStore.getState().logout();
}

async function refreshToken(): Promise<void> {
  const { refreshToken: token, user, setAuth } = useAuthStore.getState();
  if (!token) throw new Error('no refresh token');
  // GatePass 토큰 갱신 — POST /api/gatepass/refresh
  const res = await axios.post(
    `${API_BASE}/api/gatepass/refresh`,
    { refreshToken: token },
    { withCredentials: true },
  );
  const d = res.data?.data;
  if (!d?.accessToken || !user) throw new Error('refresh failed');
  // refresh 응답엔 user 정보가 없으므로 기존 user 보존
  setAuth({ accessToken: d.accessToken, refreshToken: d.refreshToken ?? token, user });
}

// 동시 refresh 가드(single-flight) — 토큰 만료 직후 병렬 401이 회전 refresh 토큰을 경쟁하지 않도록
// 한 번의 refresh Promise를 공유한다. (없으면 회전+재사용탐지에 걸려 헛 로그아웃 발생)
let refreshing: Promise<void> | null = null;
function refreshTokenOnce(): Promise<void> {
  if (!refreshing) {
    refreshing = refreshToken().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

// 요청 인터셉터 — 동기 토큰 주입 (비면제 경로만)
apiClient.interceptors.request.use((config) => {
  if (!isAuthExempt(config.url)) {
    const token = useAuthStore.getState().accessToken;
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  // 로컬 개발(FlowGate 미경유) 전용 — 게이트웨이가 JWT 검증 후 주입하는 X-Comp-Id/X-User-Id를
  // FE가 대신 붙인다. Customer 등 일부 서비스는 이 헤더가 없으면(폴백 없음) 401을 던지므로 필요.
  // VITE_DEV_BYPASS_AUTH가 아닌 환경(dev/prod 배포)에서는 절대 붙지 않는다 — 신원 스푸핑 방지.
  if (import.meta.env.VITE_DEV_BYPASS_AUTH === 'true') {
    const user = useAuthStore.getState().user;
    if (user) {
      config.headers['X-Comp-Id'] = String(user.compId);
      config.headers['X-User-Id'] = String(user.memberId);
      // Schedule 등 hasAnyRole("OWNER","STAFF") 게이트 통과용 (GatewayAuthenticationFilter가 변환)
      config.headers['X-Roles'] = user.masterYn === 'Y' ? 'OWNER' : 'STAFF';
    }
  }
  return config;
});

apiClient.interceptors.response.use(
  (res) => {
    const requestId = extractRequestId(res);
    if (requestId && res.data && typeof res.data === 'object') {
      (res.data as { requestId?: string }).requestId = requestId;
    }
    return res;
  },
  async (err) => {
    if (err.response?.status === 401 && err.config && !err.config._retry) {
      // 세션 축출/채널 누락 → refresh 재시도 없이 즉시 하드 로그아웃.
      // 단, refresh·login 등 인증 면제 요청에서 난 401은 기존 흐름대로(무한루프 방지).
      const code = extractErrorCode(err.response?.data);
      if (!isAuthExempt(err.config.url) && code && HARD_LOGOUT_CODES.includes(code)) {
        forceHardLogout();
        return Promise.reject(err);
      }
      err.config._retry = true;
      try {
        await refreshTokenOnce();
        const token = useAuthStore.getState().accessToken;
        if (token) err.config.headers.Authorization = `Bearer ${token}`;
        return apiClient.request(err.config);
      } catch {
        // 갱신 실패 → 로그아웃 처리하고 원에러 전파
        useAuthStore.getState().logout();
      }
    }
    return Promise.reject(err);
  },
);
